/**
 * Data layer for `empty-states-with-cta`. Thin typed fetch wrappers over /api/v1/empty-states-with-cta.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface EmptyStatesWithCtaRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListEmptyStatesWithCtaResponse {
	count: number;
	items: EmptyStatesWithCtaRow[];
}

export interface CreateEmptyStatesWithCtaBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/empty-states-with-cta";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListEmptyStatesWithCtaResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListEmptyStatesWithCtaResponse>(res);
}

export async function create(body: CreateEmptyStatesWithCtaBody): Promise<EmptyStatesWithCtaRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<EmptyStatesWithCtaRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const emptyStatesWithCtaQueryKeys = {
	all: ["empty-states-with-cta"] as const,
	list: () => [...emptyStatesWithCtaQueryKeys.all] as const,
	detail: (id: string) => [...emptyStatesWithCtaQueryKeys.all, id] as const,
};
